"use client";

import { useEffect, useRef } from "react";
import { captureClientEvent } from "modules/common/analytics/client";
import { POSTHOG_EVENTS } from "modules/common/analytics/events";
import type { FunnelContext } from "./VisitorFunnelCopy";

/** The two mobile bar variants, plus the desktop sidebar card. */
export type VisitorCtaSurface = "fixed" | "stacked" | "card";

/**
 * `visitor_cta_viewed` once per mount, the first time the surface is actually
 * on screen for an anonymous viewer (`isActive`), and a click handler for
 * `visitor_cta_clicked`. "Viewed" means mounted-and-visible, not scrolled
 * into view — the bar is always on screen and the card is above the fold.
 *
 * Mounted is enough: no page mounts a bar under a hidden ancestor. The pages
 * that keep a desktop branch in the DOM (`/stores/[slug]`, city) render their
 * sections through the `maxItems` / `embedded` early returns, which stop short
 * of the bar. `scripts/visitor-cta.test.mjs` pins which file mounts which
 * variant, so a new call site is what would have to change that.
 */
export function useVisitorCtaEvents(
  context: FunnelContext,
  surface: VisitorCtaSurface,
  isActive: boolean
): () => void {
  const { kind, direction } = context;
  const hasFiredView = useRef(false);

  useEffect(() => {
    if (!isActive || hasFiredView.current) return;
    hasFiredView.current = true;
    captureClientEvent(POSTHOG_EVENTS.VISITOR_CTA_VIEWED, {
      kind,
      direction,
      surface,
    });
  }, [isActive, kind, direction, surface]);

  return () =>
    captureClientEvent(POSTHOG_EVENTS.VISITOR_CTA_CLICKED, {
      kind,
      direction,
      surface,
    });
}
